import { Badge, Box, Center, HStack, Icon, Text, VStack, Link as TriggerLink } from "@chakra-ui/react"
import { FaWandMagic, FaTruckDroplet } from "react-icons/fa6"
import { FiExternalLink } from "react-icons/fi"
import { useNavigate } from "react-router-dom"
import { useNavigation } from "../../providers/NavigationProvider"
import { Tooltip } from "@hydrologiq/shared/chakra/Tooltip"
import { Asset, AssetKind } from "@hydrologiq/gql-experience/graphql"
import { nowDateTime } from "@hydrologiq/common/helpers/date"
import { hours } from "timezonecomplete"
import { getEnumKeyByEnumValue } from "@hydrologiq/shared/attributes/enum"
import { toText } from "./Kind"
import { useCopyValues } from "@hydrologiq/shared/providers/helpers/useCopy"
import { DEFAULT_COPY_MAPPINGS, COPY_KEYS } from "@hydrologiq/shared/copy/constants"
import AssetLink from "../AssetLink"
import { Link } from "@hydrologiq/shared/components/Link"
import Popover from "@hydrologiq/shared/chakra/Popover"

export function formatRunDryMinutes(minutes: number, full: boolean = false): string {
  const days = Math.floor(minutes / (24 * 60))
  const hours = Math.floor(minutes / 60)

  const dayString = full ? " day" + (days !== 1 ? "s" : "") : "d"
  const hourString = full ? " hour" + (hours !== 1 ? "s" : "") : "h"

  if (hours >= 5 * 24) {
    return `${days}${dayString}`
  } else if (hours >= 3 * 24) {
    return `${days}${dayString} ${Math.floor(hours % 24)}${hourString}`
  } else if (hours > 0) {
    return `${Math.floor(hours)}${hourString}`
  } else if (minutes >= 0) {
    return `0${hourString}`
  } else {
    return "pending"
  }
}

export interface RunDryProps {
  enabled?: boolean
  name?: string
  tooltip?: boolean
  asset?: Asset | { id: string; name: string }
  system?: { id?: string; name?: string } | string
  systemId?: string
  minutes?: number
  icon?: boolean
  iconType?: "wand" | "truck"
}

export function RunDry({
  tooltip = true,
  name,
  enabled = true,
  asset,
  system,
  systemId,
  minutes,
  icon = true,
  iconType = "wand",
}: RunDryProps) {
  // Read run_dry from asset attributes or explicit minutes
  const assetRecord = asset as Asset | undefined
  const runDryMinutes =
    minutes !== undefined
      ? minutes
      : assetRecord && assetRecord.run_dry_minutes
        ? Number(assetRecord.run_dry_minutes)
        : undefined
  const runDryHours = runDryMinutes !== undefined && runDryMinutes >= 0 ? Math.floor(runDryMinutes / 60) : undefined

  if (!enabled || (runDryMinutes === undefined && !asset && !system)) {
    return <Text color="gray.400">n/a</Text>
  }

  const formattedTime = runDryMinutes !== undefined && runDryMinutes >= 0 ? formatRunDryMinutes(runDryMinutes) : "pending"
  const systemName = typeof system === "string" ? system : system?.name
  const effectiveSystemId = systemId || (typeof system === "object" ? system?.id : undefined)
  const isTruck = iconType === "truck" || (!asset && Boolean(systemName))

  const tooltipPrefix = isTruck ? "Refuel needed in" : "Asset depleted in"
  const tooltipContent = `${tooltipPrefix} ${formattedTime}`

  const copy = useCopyValues({
    [COPY_KEYS.HEADINGS.RUN_DRY]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.RUN_DRY],
    [COPY_KEYS.HEADINGS.ASSET]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ASSET],
  })

  const hasPopoverContent = Boolean(asset || systemName)

  let navigate: ReturnType<typeof useNavigate> | null = null
  try {
    navigate = useNavigate()
  } catch {
    navigate = null
  }
  const hubNavigate = useNavigation()

  const systemUrl = effectiveSystemId ? `/systems/${effectiveSystemId}` : ""

  const handleSystemNavigate = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!systemUrl) return
    if (hubNavigate) {
      hubNavigate(systemUrl)
    } else if (navigate) {
      navigate(systemUrl)
    }
  }

  const handleSystemOpenNewTab = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!systemUrl) return
    window.open(systemUrl, "_blank", "noopener,noreferrer")
  }

  const trigger = (
    <HStack
      gap="0.35em"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      cursor={hasPopoverContent ? "pointer" : tooltip ? "help" : "default"}
      fontSize="1em"
      lineHeight="1.2"
    >
      {tooltip && runDryMinutes !== undefined ? (
        <Tooltip content={tooltipContent}>
          <HStack gap="0.35em" display="inline-flex" alignItems="center" cursor={hasPopoverContent ? "pointer" : "help"}>
            <TriggerLink
              variant="secondary"
              _hover={!hasPopoverContent ? { textDecoration: "none", cursor: "help" } : undefined}
              display="inline-flex"
              alignItems="center"
              fontSize="1em"
              lineHeight="1.2"
              cursor={hasPopoverContent ? "pointer" : "help"}
            >
              {formattedTime}
              {icon && (
                <Center ml="0.25em" display="inline-flex">
                  <Icon
                    boxSize={isTruck ? "1.15em" : "1em"}
                    as={isTruck ? FaTruckDroplet : FaWandMagic}
                    transform={isTruck ? undefined : "rotate(-45deg)"}
                    verticalAlign="middle"
                  />
                </Center>
              )}
            </TriggerLink>
          </HStack>
        </Tooltip>
      ) : (
        <TriggerLink
          variant="secondary"
          _hover={!hasPopoverContent ? { textDecoration: "none", cursor: "default" } : undefined}
          display="inline-flex"
          alignItems="center"
          fontSize="1em"
          lineHeight="1.2"
        >
          {formattedTime}
          {icon && (
            <Center ml="0.25em" display="inline-flex">
              <Icon
                boxSize={isTruck ? "1.15em" : "1em"}
                as={isTruck ? FaTruckDroplet : FaWandMagic}
                transform={isTruck ? undefined : "rotate(-45deg)"}
                verticalAlign="middle"
              />
            </Center>
          )}
        </TriggerLink>
      )}
    </HStack>
  )

  if (!hasPopoverContent) {
    return trigger
  }

  return (
    <Popover
      trigger={trigger}
      showArrow={true}
      placement="bottom"
      width="18.75rem"
      closeOnOutside
    >
      <Box
        bg="white"
        borderRadius="md"
        border="1px solid"
        borderColor="gray.200"
        boxShadow="0 1px 2px rgba(0, 0, 0, 0.04)"
        p="0.75em"
        width="100%"
      >
        <VStack gap="0.5em" width="100%" align="stretch" fontSize="1em">
          {systemName && (
            <HStack justify="space-between" align="center" width="100%" py="0.125rem">
              <Text fontSize="0.875rem" color="gray.600" fontWeight="500">
                System
              </Text>
              {effectiveSystemId ? (
                <Badge
                  size="sm"
                  variant="subtle"
                  bg="#EDF2F7"
                  color="#4A5568"
                  display="inline-flex"
                  alignItems="center"
                  gap="0.25rem"
                  px={2}
                  py={0.5}
                  borderRadius="sm"
                >
                  <Text
                    as="span"
                    cursor="pointer"
                    fontWeight="medium"
                    fontSize="0.8125rem"
                    _hover={{ textDecoration: "underline", color: "#1A365D" }}
                    onClick={handleSystemNavigate}
                    aria-label={`Go to ${systemName}`}
                  >
                    {systemName}
                  </Text>
                  <Box
                    as="button"
                    type="button"
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center"
                    cursor="pointer"
                    p="3px"
                    ml="2px"
                    borderRadius="xs"
                    _hover={{ bg: "#BEE3F8", color: "#1A365D" }}
                    transition="all 0.15s ease"
                    onClick={handleSystemOpenNewTab}
                    aria-label={`Open ${systemName} in new tab`}
                  >
                    <FiExternalLink size="11px" />
                  </Box>
                </Badge>
              ) : (
                <Text fontSize="0.875rem" fontWeight="500">{systemName}</Text>
              )}
            </HStack>
          )}

          {asset && (
            <>
              <HStack justify="space-between" align="center" width="100%" py="0.125rem">
                <Text fontSize="0.875rem" color="gray.600" fontWeight="500">
                  {copy[COPY_KEYS.HEADINGS.ASSET]}
                </Text>
                {"kind" in asset ? (
                  <AssetLink asset={asset as Asset} openExternal={true} />
                ) : (
                  <Link variant="primary" href={`/assets/${asset.id}`} fontSize="0.875rem">
                    {asset.name}
                  </Link>
                )}
              </HStack>

              {"kind" in asset && asset.kind && (
                <HStack justify="space-between" align="center" width="100%" py="0.125rem">
                  <Text fontSize="0.875rem" color="gray.600" fontWeight="500">Type:</Text>
                  <Text fontSize="0.875rem">{toText(getEnumKeyByEnumValue(AssetKind, asset.kind))}</Text>
                </HStack>
              )}

              {runDryHours !== undefined && (
                <>
                  <HStack justify="space-between" align="center" width="100%" py="0.125rem">
                    <Text fontSize="0.875rem" color="gray.600" fontWeight="500">Depletion hours:</Text>
                    <Text fontSize="0.875rem">{runDryHours} hrs</Text>
                  </HStack>
                  <HStack justify="space-between" align="center" width="100%" py="0.125rem">
                    <Text fontSize="0.875rem" color="gray.600" fontWeight="500">Depletion date:</Text>
                    <Text fontSize="0.875rem">{nowDateTime().add(hours(runDryHours)).format("d/MM/yyyy")}</Text>
                  </HStack>
                </>
              )}
            </>
          )}
        </VStack>
      </Box>
    </Popover>
  )
}

export default RunDry
